import { makeStyles } from '@mui/styles';
import { CircularProgress } from 'apollo-react';
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import Export from 'apollo-react-icons/Export';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ExternalDataSourceTable from './ExternalDataSourceTable';
import { setQuery, setSourceDataList, setStatus } from '../../actions/ConfigDatasourcesActions';
import { handleErrors } from '../../actions/ErrorActions';
import {
  exportExcelSourceData,
  getSourceData,
  saveConfigDatasource,
} from '../../api/config-datasources';
import { EXCEL_FILE_EXTENSION } from '../../constants/SavedSearches';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getConfigureExternalDatasources } from '../../selectors/ConfigureDataSourcesSelectors';
import { invokeBlobFileDownloading } from '../../utils/commonUtils';
import { getContentDispositionFile } from '../../utils/savedSearchUtils';

const useStyles = makeStyles({
  content: {
    width: '100%',
    minHeight: 150,
    marginTop: '16px',
  },
  modal: {
    maxWidth: 'unset',
  },
  table: {
    border: '1px solid #e9e9e9',
    width: '100%',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: '-20px',
    width: '100%',
  },
  exportPending: {
    marginTop: 3,
  },
});

const ExternalDataDisplayModal = ({ open, onClose, source, updateQueryInSourceTableRow }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const {
    query,
    sourceDataList,
    status: { isQueryResultLoading, isExporting, enableQueryAction },
  } = useSelector(getConfigureExternalDatasources);

  const [localQuery, setLocalQuery] = useState(query);

  useEffect(() => {
    const loadSourceListData = async () => {
      dispatch(setSourceDataList([]));
      if (source && open) {
        dispatch(setStatus({ isQueryResultLoading: true }));
        const response = await getSourceData(source.source);
        if (!response.hasError) {
          dispatch(setSourceDataList(response.resource));
        } else {
          dispatch(handleErrors(response.errors));
          dispatch(setSourceDataList([]));
        }
        dispatch(setStatus({ isQueryResultLoading: false }));
      }
    };

    loadSourceListData();
  }, [source, open, dispatch]);

  useEffect(() => {
    if (source) {
      dispatch(setQuery(source.query || ''));
      setLocalQuery(source.query || '');
      dispatch(setStatus({ enableQueryAction: !false }));
    }
  }, [source, dispatch]);

  const handleQueryChange = (event) => {
    const updatedQuery = event.target.value;
    setLocalQuery(updatedQuery);
  };

  const handleSaveAndTestSQL = async () => {
    if (localQuery.trim() === '') {
      return;
    }
    dispatch(setStatus({ isQueryResultLoading: true }));
    const payload = [{ ...source, query: localQuery }];

    try {
      const saveResponse = await saveConfigDatasource(payload);
      if (!saveResponse.hasError) {
        updateQueryInSourceTableRow(source, localQuery);
        const executeResponse = await getSourceData(source.source);
        if (!executeResponse.hasError) {
          dispatch(setSourceDataList(executeResponse.resource));
          dispatch(setQuery(localQuery));
          dispatch(setStatus({ enableQueryAction: true }));
        } else {
          dispatch(setSourceDataList([]));
          dispatch(handleErrors(executeResponse.errors));
        }
      } else {
        dispatch(handleErrors(saveResponse.errors));
      }
    } catch (error) {
      dispatch(handleErrors([error.message]));
    }

    dispatch(setStatus({ isQueryResultLoading: false }));
  };

  const handleQueryBlur = () => {
    dispatch(setQuery(localQuery));
    dispatch(setStatus({ enableQueryAction: source.query.trim() === localQuery.trim() }));
  };

  const handleExport = async () => {
    try {
      dispatch(setStatus({ isExporting: true }));
      const { data: apiData, headers } = await exportExcelSourceData(source.source);

      if (apiData instanceof Blob && headers['content-type'] === 'application/json') {
        const text = await apiData.text();
        const json = JSON.parse(text);

        if (json.hasError) {
          dispatch(handleErrors(json.errors));
        }
      } else {
        const contentDisposition = headers['content-disposition'];
        const contentDispositionFile = getContentDispositionFile(contentDisposition);
        const defaultFileName = source.source || contentDispositionFile?.name;
        const fileExtension = contentDispositionFile?.extension || EXCEL_FILE_EXTENSION;
        invokeBlobFileDownloading(apiData, `${defaultFileName}.${fileExtension}`);
      }
    } catch (error) {
      dispatch(handleErrors(error));
    }
    dispatch(setStatus({ isExporting: false }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translateString('External Data')}
      disableBackdropClick
      disableEscapeKeyDown
      className={classes.modal}
      variant="default"
      buttonProps={[
        { label: translateString('Cancel'), 'ui-ta': 'Datasource-CancelButton' },
        {
          label: translateString('Export'),
          variant: 'secondary',
          onClick: handleExport,
          disabled: !(sourceDataList.rows && sourceDataList.rows.length > 0),
          icon: !isExporting ? (
            <Export />
          ) : isExporting ? (
            <CircularProgress
              className={classes.exportPending}
              color="inherit"
              variant="indeterminate"
              size={20}
            />
          ) : null,
          'ui-ta': 'Datasource-SaveAndTestButton',
        },
        {
          label: translateString('Save and Test SQL'),
          variant: 'secondary',
          onClick: handleSaveAndTestSQL,
          disabled: enableQueryAction || localQuery.trim() === '',
          'ui-ta': 'Datasource-SaveAndTestButton',
        },
      ]}
    >
      <div className={classes.formGroup}>
        <TextField
          label={translateString('SQL Statement')}
          value={localQuery}
          onChange={handleQueryChange}
          onBlur={handleQueryBlur}
          fullWidth
          minRows={1.5}
          sizeAdjustable={true}
          multiline
          minWidth={1000}
        />
      </div>

      <div className={classes.content}>
        <div className={classes.table}>
          <ExternalDataSourceTable
            isLoading={isQueryResultLoading}
            source={source}
            sourceDataList={sourceDataList}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ExternalDataDisplayModal;
